/**
 * The corporate-hierarchy client — admin CRUD over the company-ownership table.
 *
 * WHAT THIS TABLE IS, because its neighbour is its exact opposite:
 *   · `/admin/parties` (the merge queue) decides "these two ROWS are the SAME company".
 *   · `corporate_hierarchy` states "these two companies are DIFFERENT, and one OWNS the other" —
 *     e.g. child "SONY/ATV TUNES LLC" is owned by parent "SONY MUSIC PUBLISHING". Merging them
 *     would be wrong; the subsidiary is the party that appears on registrations.
 *
 * TWO AXES PER ROW since 2026-08-27 (migration `2026-08-27_corporate_hierarchy_acts_as.sql`), and
 * keeping them apart is the whole point of this module:
 *   · `relationship` — what a company IS to its parent. CORPORATE. owns | joint_venture |
 *     imprint_of. Does not vary by country.
 *   · `acts_as` — what a company DOES for it. A MANDATE. publisher | sub_publisher |
 *     administrator | distributor | label, or NULL. Varies by country, which is why it lives on
 *     the EDGE and inherits the edge's `territory`.
 * They are independently true: GEMA states WARNER CHAPPELL MUSIC GERMANY GMBH acts as a
 * SUB-PUBLISHER, and this map separately states it is OWNED BY Warner Chappell Music Group. Both
 * correct. `administers` and `distributes` used to be values of `relationship`, where — because
 * one column holds one value — recording either SILENTLY DESTROYED the ownership fact. They moved
 * to `acts_as` as `administrator`/`distributor`, and left this vocabulary.
 *
 * Honesty rules the callers must obey (owner decisions, spec
 * docs/superpowers/specs/2026-08-21-track-rights-chain-panel-design.md):
 *   · NO ROW ⇒ "no parent on file". It is NOT "independent" — we cannot tell those apart, and no
 *     registry states it. Never infer, default, or soften.
 *   · `territory` null/empty ⇒ GLOBAL (applies everywhere). It never means "unknown".
 *   · `acts_as` null ⇒ NOT RECORDED. Never "none", and never "publisher by default". 205 of the
 *     405 companies have a registry-stated role sitting in `source_party_relationships`, but
 *     evidence is not canon: it may be OFFERED for one-click acceptance and is written only when a
 *     person confirms it. Defaulting this field would manufacture 408 facts nobody stated.
 *   · `songs_covered` is the SOURCE's own count over the SOURCE's catalogue; it drifts from ours
 *     and is never rendered anywhere in the product (OD-8.2). It stays on this type only because
 *     the wire returns it.
 *   · `source_label` distinguishes a hand-made row ('admin') from a loaded one ('owner_curated'),
 *     which is what lets a loader reload prune only its own rows. The admin page marks the
 *     hand-made ones and leaves the loaded ones unmarked — printing the default on all 408 rows
 *     said nothing 408 times.
 *
 * Wraps the admin-gated backend endpoints:
 *   - GET    /api/admin/corporate-hierarchy        (paginated, q + territory filters)
 *   - POST   /api/admin/corporate-hierarchy        (409 when the edge already exists)
 *   - PATCH  /api/admin/corporate-hierarchy/:id    (404 absent, 409 collision)
 *   - DELETE /api/admin/corporate-hierarchy/:id    (404 absent)
 *   - PUT    /api/admin/company-contacts           (the facts about ONE COMPANY, keyed by name)
 *   - GET    /api/admin/corporate-hierarchy/groups/assign-preview?group=   (what an assign would do)
 *   - POST   /api/admin/corporate-hierarchy/groups/assign                  (assign a reference group)
 *
 * All of them require the ADMIN account-type; callers must be routed behind <AdminGuard>.
 * Errors surface as ApiError (see api-client.ts).
 *
 * WHY THE CONTACT ENDPOINT TAKES A NAME AND NOT AN EDGE ID (2026-08-27)
 * A contact is a fact about a COMPANY. The live map is 408 edges over just 26 parents, and
 * `Sony Music Publishing Group` is the parent on 94 of them — an edge-keyed address would be 94
 * copies to keep in step, and deleting any one edge would take the address with it, while the page
 * promises the opposite in words ("only the link between them goes"). So contacts are stored under
 * the normalised company name, arrive on every row as `child_contact_email`/`parent_contact_email`,
 * and are written by naming the company.
 *
 * THE IDENTIFIER JOINED THEM THE SAME DAY, for the same reason: an IPI/CAE or an ISNI identifies a
 * COMPANY, so Sony's number should not be typed on 94 rows either. The consequence to hold on to
 * is that one record now carries up to THREE independent facts, so a save must name only what it
 * changed — see `CompanyFactsPatch`. Sending every field on every save would make clearing an
 * address destroy an identifier nobody asked to remove.
 */
import { apiClient } from "./api-client";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The three CORPORATE words a hierarchy row can be marked with, read PARENT-FIRST:
 * `parent_name` <relationship> `child_name`. The UI prints the CHILD's side of it ("owned by",
 * "an imprint of"), because the child is the row's subject — see RELATIONSHIP_COPY. Storing a
 * row the other way round is silent corruption, so this direction is written down here, in
 * `corporate_hierarchy_service.py` and in the migration, and pinned by a test on each side.
 *
 * Mirrors the database CHECK. `owns` is the default and every one of the 408 live rows.
 *
 * `imprint_of` joined the vocabulary on 2026-08-27. It is the one mark that is not a relationship
 * between two COMPANIES: an imprint is a label BRAND with no separate legal entity behind it —
 * Def Jam under Universal, Verve under UMG — so there is nothing to own and `owns` would overstate
 * it, and it is not administration either.
 *
 * WHAT LEFT, the same day: `administers` and `distributes`. They are MANDATES, not corporate
 * facts, and they are now `acts_as = 'administrator' | 'distributor'` — see ACTS_AS. A stored row
 * still carrying one of them prints as the raw word (`relationshipCopy` degrades), never as
 * ownership; none exist.
 */
export const RELATIONSHIPS = ["owns", "joint_venture", "imprint_of"] as const;

export type CorporateHierarchyRelationship = (typeof RELATIONSHIPS)[number];

/**
 * The five MANDATE words — what a company DOES for its parent, in this row's territory.
 *
 * Mirrors the `corporate_hierarchy_acts_as_check` CHECK and the API's `ActsAsLiteral`. A separate
 * axis from RELATIONSHIP, not more values of it: a company can be OWNED BY its parent and act as
 * that parent's SUB-PUBLISHER in Germany at the same time, and one column could only ever have
 * kept one of the two.
 *
 * NULL/absent means NOT RECORDED. There is no default, and adding one would state 408 facts
 * nobody made.
 */
export const ACTS_AS = [
  "publisher",
  "sub_publisher",
  "administrator",
  "distributor",
  "label",
] as const;

export type CorporateHierarchyActsAs = (typeof ACTS_AS)[number];

/**
 * One ownership edge. Every field except `child_name`, `parent_name`, `territory`,
 * `territory_code`, `relationship` and `notes` is SERVER-DERIVED — the normalized forms, the
 * resolved party ids and the source label are computed by the backend and never sent by this
 * client.
 */
export interface CorporateHierarchyRow {
  id: string;
  child_name: string;
  child_normalized: string;
  child_party_id: string | null;
  parent_name: string;
  parent_normalized: string;
  parent_party_id: string | null;
  /** null ⇒ GLOBAL (all territories). Never "unknown". */
  territory: string | null;
  /** ISO-3166-1 alpha-2, or null for a region that has no single code (Benelux, Latin America). */
  territory_code: string | null;
  /**
   * One of RELATIONSHIPS. Typed as a plain string on the wire on purpose: the database may admit a
   * word this build has never heard of, and a union type would make TypeScript insist otherwise
   * while the UI printed nothing. `relationshipCopy` degrades to showing the raw word.
   */
  relationship: string;
  /**
   * The MANDATE — one of ACTS_AS, or null for NOT RECORDED. Typed as a plain string for the same
   * reason as `relationship` above: the database may admit a word this build has never heard of.
   *
   * Optional on this type, not because null and absent mean the same thing — they do not — but
   * because a row read from an older response simply has no such key, and a component that reads
   * `row.acts_as` must be written to survive that. `actsAsLabel` treats both as "not recorded".
   */
  acts_as?: string | null;
  /**
   * What a REGISTRY says this company does — PRS/GEMA/MLC, out of `source_party_relationships`.
   *
   * EVIDENCE, NEVER CANON: it is offered for one-click acceptance and written only when a person
   * confirms it (see `ActsAsEvidence`). Optional because the list endpoint does NOT return it
   * today; `readActsAsEvidence` returns null for every row until it does, and the chip renders
   * nothing rather than asserting a registry said something it did not.
   */
  child_acts_as_evidence?: unknown;
  /**
   * 'admin' for rows made on this page, 'owner_curated' for loader rows, 'org_self_served' for a
   * row an organization added to its own group.
   */
  source_label: string;
  /**
   * The organization whose group this row is in (`corporate_hierarchy.organization_id`), or null for
   * a ZenMusic reference row nobody has been assigned. It routes NOTHING — a claim does — but it is
   * what puts the row in that org's Your companies. Optional: older responses omit it.
   */
  organization_id?: string | null;
  /** That organization's name, read once per page by the admin list. */
  organization_name?: string | null;
  /**
   * The address for the CHILD company, or null when we hold none. It belongs to the COMPANY and
   * not to this row — the same company on another row carries the same value, and editing it here
   * changes it everywhere. Always present on the wire; null is "we hold none".
   */
  child_contact_email: string | null;
  /** The same, for the PARENT — usually the one the escalation question is about. */
  parent_contact_email: string | null;
  /**
   * The CHILD company's IPI/CAE — the CISAC interested-party number the PROs key royalties on.
   * 9-11 digits, stored without separators. Company-level exactly like the address above.
   */
  child_ipi_number: string | null;
  /**
   * The CHILD company's ISNI — the ISO identifier for a public identity. 16 characters, stored
   * unspaced; `formatIsni` re-applies the published grouping for display.
   *
   * A SECOND field and not an alternative to the IPI: the two are different registries answering
   * different questions and a company routinely has both.
   */
  child_isni: string | null;
  /** The same two, for the PARENT. */
  parent_ipi_number: string | null;
  parent_isni: string | null;
  /**
   * The SOURCE's own song count. NEVER RENDERED (OD-8.2) — it counts the source's catalogue, not
   * ours, so it is wrong the moment our catalogue differs. Present only because the wire has it.
   */
  songs_covered: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CorporateHierarchyListResponse {
  items: CorporateHierarchyRow[];
  total: number;
  has_more: boolean;
}

/** The writable fields. Everything else on a row is derived server-side. */
export interface CorporateHierarchyInput {
  child_name: string;
  parent_name: string;
  territory?: string | null;
  /**
   * Blank/omitted means DERIVE from the territory, NOT "no code" — clearing a code is done by
   * clearing the territory, which is the fact the code restates. A typed value beats the
   * derivation, which is the point: the server's table knows 30 territories, not every territory.
   */
  territory_code?: string | null;
  /** Omitted means 'owns' — the default the whole table was built on. */
  relationship?: string | null;
  /**
   * The MANDATE. Omitted on CREATE means NOT RECORDED (the column is left NULL) — there is no
   * default, because a default would be a claim.
   *
   * On PATCH the omitted-vs-null distinction is load-bearing and the server reads the body with
   * `exclude_unset`: OMITTING it leaves the stored mandate alone, an explicit `null` CLEARS it.
   * Those are different instructions and the UI must never collapse them — clearing is the only
   * way to take back a mandate that turned out to be wrong.
   */
  acts_as?: string | null;
  notes?: string | null;
}

/** A PATCH sends only what changed; every field is optional. */
export type CorporateHierarchyPatch = Partial<CorporateHierarchyInput>;

export interface CorporateHierarchyListParams {
  /** Case-insensitive substring over child_name OR parent_name. */
  q?: string;
  territory?: string;
  /** 1–200 server-side. */
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

// The server wraps single-row responses; callers get the row itself.
interface CorporateHierarchyItemResponse {
  item: CorporateHierarchyRow;
}

interface CorporateHierarchyDeleteResponse {
  deleted: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Client functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch ONE page of ownership edges. The server caps `limit` at 200 and orders by
 * (child_name, territory, id), so paging is stable — page 2 cannot repeat or skip a row.
 *
 * `q` and `territory` are the endpoint's own filters. The admin page no longer uses either (it
 * groups the whole map and filters in memory — see `loadCorporateHierarchyMap`); they stay on this
 * wrapper because they are part of the request contract, and `territory=global` is the only way to
 * reach the rows whose territory is NULL.
 *
 * `signal` lets a caller drop a stale response: a slow early request must not overwrite the
 * results of a faster later one.
 */
export async function listCorporateHierarchy(
  params: CorporateHierarchyListParams = {}
): Promise<CorporateHierarchyListResponse> {
  const { q, territory, limit, offset, signal } = params;
  const qp = new URLSearchParams();
  if (q && q.trim()) qp.set("q", q.trim());
  if (territory && territory.trim()) qp.set("territory", territory.trim());
  if (limit !== undefined) qp.set("limit", String(limit));
  if (offset !== undefined) qp.set("offset", String(offset));
  const query = qp.toString();
  return apiClient.get<CorporateHierarchyListResponse>(
    query
      ? `/api/admin/corporate-hierarchy?${query}`
      : "/api/admin/corporate-hierarchy",
    { signal }
  );
}

/** The server's own cap on `limit`, and therefore the size of one page of the map. */
export const MAP_PAGE_SIZE = 200;

/**
 * How many rows the grouped view will load. 408 live rows over 26 parents today (measured
 * 2026-08-27), so this is a ceiling with 2.4× of headroom, not a limit anyone reaches.
 *
 * STATED HERE AND SHOWN ON SCREEN when it bites, rather than silently truncating: a page that
 * groups "the whole map" while holding half of it would report wrong counts, wrong coverage and a
 * search that misses companies it claims to cover. If the map ever outgrows this, the answer is
 * server-side grouping (one row per parent), not a bigger number.
 */
export const MAP_ROW_CEILING = 1000;

export interface CorporateHierarchyMap {
  rows: CorporateHierarchyRow[];
  /** What the server says the table holds — may exceed `rows.length` at the ceiling. */
  total: number;
  /** True when `total` outran MAP_ROW_CEILING and the tail was deliberately not fetched. */
  truncated: boolean;
}

/**
 * Load the WHOLE map, for the grouped view.
 *
 * The group is the unit of this page — 26 cards over 408 links — and a group cannot be assembled
 * from a page: 94 of those links name Sony, spread across 9 server pages, so a paginated fetch can
 * never state "94 companies, 16 countries, 0 reachable" without lying on 8 of them.
 *
 * TWO ROUND TRIPS, NOT NINE. The first page also returns `total`, so every remaining page is
 * requested in PARALLEL rather than walked one after another. At 408 rows that is 1 + 2 requests
 * issued as 2 waves; the alternative walks the map serially and costs the operator the latency of
 * each page in turn.
 *
 * Deduplicated by id before returning: the ordering is stable, but a row inserted by another admin
 * between the parallel requests would otherwise be able to appear on two pages at once.
 */
export async function loadCorporateHierarchyMap(
  options: { signal?: AbortSignal } = {}
): Promise<CorporateHierarchyMap> {
  const { signal } = options;
  const first = await listCorporateHierarchy({
    limit: MAP_PAGE_SIZE,
    offset: 0,
    signal,
  });

  const total = first.total ?? first.items.length;
  const reachable = Math.min(total, MAP_ROW_CEILING);
  const offsets: number[] = [];
  for (let offset = MAP_PAGE_SIZE; offset < reachable; offset += MAP_PAGE_SIZE) {
    offsets.push(offset);
  }

  const rest = await Promise.all(
    offsets.map((offset) =>
      listCorporateHierarchy({ limit: MAP_PAGE_SIZE, offset, signal })
    )
  );

  const byId = new Map<string, CorporateHierarchyRow>();
  for (const page of [first, ...rest]) {
    for (const row of page.items) byId.set(row.id, row);
  }

  return {
    rows: [...byId.values()],
    total,
    truncated: total > MAP_ROW_CEILING,
  };
}

/**
 * Create one ownership edge. Throws ApiError 409 when the same
 * (child, parent, territory) edge already exists — the reason is in `body.detail`.
 */
export async function createCorporateHierarchyRow(
  input: CorporateHierarchyInput
): Promise<CorporateHierarchyRow> {
  const res = await apiClient.post<CorporateHierarchyItemResponse>(
    "/api/admin/corporate-hierarchy",
    input
  );
  return res.item;
}

/**
 * Patch one edge. Throws ApiError 404 when the row is gone and 409 when the edit would collide
 * with an existing edge.
 */
export async function updateCorporateHierarchyRow(
  id: string,
  patch: CorporateHierarchyPatch
): Promise<CorporateHierarchyRow> {
  const res = await apiClient.patch<CorporateHierarchyItemResponse>(
    `/api/admin/corporate-hierarchy/${encodeURIComponent(id)}`,
    patch
  );
  return res.item;
}

/** Delete one edge. Throws ApiError 404 when it is already gone. */
export async function deleteCorporateHierarchyRow(id: string): Promise<void> {
  await apiClient.delete<CorporateHierarchyDeleteResponse>(
    `/api/admin/corporate-hierarchy/${encodeURIComponent(id)}`
  );
}

/**
 * What the company endpoint stores. `set_by` is audit and never returned.
 *
 * Every fact is nullable: a row may hold an address, an identifier, or both. It never holds none —
 * the server deletes the row instead, so absence is the row's absence.
 */
export interface CompanyContact {
  id: string;
  company_name: string;
  company_normalized: string;
  contact_email: string | null;
  ipi_number: string | null;
  isni: string | null;
  created_at: string;
  updated_at: string;
}

interface CompanyContactResponse {
  item: CompanyContact | null;
}

/**
 * The facts a save may carry. OMITTING a key and passing `null` are DIFFERENT INSTRUCTIONS, and
 * keeping them apart is the whole contract of this call:
 *
 *   · omitted  → leave that fact exactly as it is stored;
 *   · null/""  → clear that one fact, and no other;
 *
 * so `{ contact_email: "a@b.com" }` cannot touch the company's IPI. Send a key only when the
 * operator actually changed it — spreading every field on every save would make an address edit
 * silently rewrite the identifier beside it.
 */
export interface CompanyFactsPatch {
  contact_email?: string | null;
  /** IPI/CAE: 9-11 digits. Spaces and dashes are fine; the server stores the digits. */
  ipi_number?: string | null;
  /** ISNI: 16 characters. The published grouping is fine; the server stores it unspaced. */
  isni?: string | null;
}

/**
 * Record, replace or clear what we hold about a company — its address, its IPI, its ISNI. Keyed by
 * the company NAME, so this same call from any row that names the company reaches the same record.
 *
 * PUT and not PATCH: the body carries the whole of what it NAMES, and sending it twice leaves one
 * row in one state. The server reads it with `exclude_unset`, which is what makes the omitted-vs-
 * null distinction above real rather than a convention.
 *
 * Resolves to `null` when the company is left recording NOTHING — the server deletes the row
 * rather than blanking it, so absence stays absence rather than becoming empty strings that render
 * as facts we do not have. A clear that leaves something behind resolves to the surviving row.
 *
 * Throws ApiError 400 when a value is not what it claims to be (the sentence is in `body.detail`).
 */
export async function setCompanyContact(
  companyName: string,
  facts: CompanyFactsPatch
): Promise<CompanyContact | null> {
  const res = await apiClient.put<CompanyContactResponse>(
    "/api/admin/company-contacts",
    { company_name: companyName, ...facts }
  );
  return res.item;
}

// ─────────────────────────────────────────────────────────────────────────────
// Display helpers — the honesty rules, in one place so every surface agrees
// ─────────────────────────────────────────────────────────────────────────────

/** What a company with no ownership row on file reads as. NEVER "Independent". */
export const NO_PARENT_ON_FILE = "No parent on file";

/** What an empty `territory` reads as. It means everywhere, not unknown. */
export const GLOBAL_TERRITORY_LABEL = "Global (all territories)";

/**
 * What a company we hold no address for reads as.
 *
 * Deliberately NOT the same treatment as an absent territory code, which renders as nothing: that
 * emptiness is a FACT (a region like Benelux has no single ISO code), whereas a missing address is
 * simply something nobody has found yet. Saying so makes the contact column a worklist instead of
 * a puzzle about whether the data failed to load.
 */
export const NO_CONTACT_ON_FILE = "None on file";

/**
 * What a company we hold no MANDATE for reads as. Same treatment and same reason as the address
 * above: nobody has recorded it yet, which is a worklist entry, not a fact about the company.
 */
export const NO_ACTS_AS_ON_FILE = "Not recorded";

/**
 * A parent name for display. An absent/blank name means we hold no ownership record — which is NOT
 * a claim of independence, so it renders as "No parent on file".
 */
export function formatParentName(parentName: string | null | undefined): string {
  const trimmed = (parentName ?? "").trim();
  return trimmed || NO_PARENT_ON_FILE;
}

/**
 * A territory for display. NULL/blank is GLOBAL by definition of the column — the loader writes
 * NULL for worldwide arrangements — so it must never render as "Unknown" or a bare dash.
 */
export function formatTerritory(territory: string | null | undefined): string {
  const trimmed = (territory ?? "").trim();
  return trimmed || GLOBAL_TERRITORY_LABEL;
}

interface RelationshipCopy {
  /** what the picker shows, from the PARENT's side (the direction that is stored) */
  option: string;
  /** what a row prints above the counterparty, from the CHILD's side */
  phrase: string;
  /** what a GROUP CARD prints once for all of its rows: "94 companies · all owned" */
  groupPhrase: string;
  /** the counterparty field's label in the forms */
  parentLabel: string;
  /** the subject field's label — "the subsidiary" is only true for ownership */
  childLabel: string;
  /** what the mark MEANS, under the picker */
  hint: string;
}

/**
 * How each relationship reads on screen. Several strings per word, because the same fact is
 * phrased differently in a picker, on a row, on a card header and on a form label, and letting each
 * surface invent its own wording is how "owned by" and "owns" end up on the same screen meaning
 * opposite directions.
 *
 * THREE WORDS SINCE 2026-08-27, not five. `administers` and `distributes` were mandates sitting in
 * the corporate column and are now ACTS_AS values; see ACTS_AS_COPY for their replacements.
 */
export const RELATIONSHIP_COPY: Record<
  CorporateHierarchyRelationship,
  RelationshipCopy
> = {
  owns: {
    option: "Owns (corporate parent)",
    phrase: "owned by",
    groupPhrase: "owned",
    parentLabel: "Owned by (the parent)",
    childLabel: "Company (the subsidiary)",
    hint: "Corporate ownership. This is the only mark that appears on a track's rights panel.",
  },
  joint_venture: {
    option: "Joint venture partner",
    phrase: "joint venture with",
    groupPhrase: "joint ventures",
    parentLabel: "Joint venture with",
    childLabel: "Company",
    hint:
      "The company is jointly held. Add one row per partner — neither one is the sole owner. " +
      "Recorded here for staff; the rights panel never shows it as an owner.",
  },
  imprint_of: {
    option: "Imprint of (a label brand)",
    phrase: "an imprint of",
    groupPhrase: "imprints",
    parentLabel: "An imprint of",
    // Not "the subsidiary": an imprint is not a company at all, and calling it one here is how the
    // wrong model gets taught to whoever fills this form in next.
    childLabel: "Imprint (the label brand)",
    hint:
      "A label brand with no separate company behind it — Def Jam under Universal, Verve under " +
      "UMG. There is nothing to own, so this is not ownership, and it is not administration. " +
      "Recorded here for staff; the rights panel never shows it as an owner.",
  },
};

/**
 * The copy for a stored relationship, degrading honestly.
 *
 * A word this build has never heard of is printed AS ITSELF rather than mapped onto "owned by".
 * That now covers the two words that LEFT this vocabulary as well as any that may join it: a row
 * still carrying `administers` reads "administers", never "owned by", which is the truth about a
 * row nobody has migrated.
 */
export function relationshipCopy(
  relationship: string | null | undefined
): RelationshipCopy {
  const key = (relationship ?? "").trim() as CorporateHierarchyRelationship;
  if (key in RELATIONSHIP_COPY) return RELATIONSHIP_COPY[key];
  const raw = key.replace(/_/g, " ");
  return {
    option: raw,
    phrase: raw,
    groupPhrase: raw,
    parentLabel: "Related company",
    childLabel: "Company",
    hint: "This build does not know this relationship, so it is shown exactly as it is stored.",
  };
}

/**
 * How each MANDATE reads on screen, and what it means.
 *
 * The hints are not decoration: `publisher` and `sub_publisher` are the pair people get wrong, and
 * whether a company is one or the other decides who can actually grant a licence in that country.
 */
export const ACTS_AS_COPY: Record<
  CorporateHierarchyActsAs,
  { label: string; hint: string }
> = {
  publisher: {
    label: "Publisher",
    hint: "Holds the publishing rights it exploits, in this territory.",
  },
  sub_publisher: {
    label: "Sub-publisher",
    hint: "Represents the parent's catalogue in this territory only. It does not own it.",
  },
  administrator: {
    label: "Administrator",
    hint: "Administers the catalogue under a deal that can end. Not an owner.",
  },
  distributor: {
    label: "Distributor",
    hint: "Distributes the releases. A distributor cannot grant anything.",
  },
  label: {
    label: "Label",
    hint: "Releases the recordings — the master side of the same group.",
  },
};

/**
 * A stored mandate as a label. Unknown words print as themselves for the same reason as above, and
 * null/blank prints as NOT RECORDED, which is never "none".
 */
export function actsAsLabel(actsAs: string | null | undefined): string {
  const key = (actsAs ?? "").trim() as CorporateHierarchyActsAs;
  if (!key) return NO_ACTS_AS_ON_FILE;
  return key in ACTS_AS_COPY ? ACTS_AS_COPY[key].label : key.replace(/_/g, " ");
}

/**
 * WHAT A REGISTRY SAYS a company does — the violet chip, and the value the editor offers for
 * one-click acceptance.
 *
 * `roles` are ACTS_AS words; `sources` names who said so ("GEMA", "MLC/PRS"). Two roles is not a
 * contradiction — the role varies by territory, and a registry may state both.
 *
 * IT IS NEVER WRITTEN AUTOMATICALLY. Evidence becomes canon only when a person confirms it — the
 * rights layer's central rule (ZR003 refuses any confirmed assertion citing a third-party source),
 * and this page is not the exception to it.
 */
export interface ActsAsEvidence {
  roles: string[];
  sources: string;
}

/**
 * Read the evidence off a row, or null.
 *
 * THE WIRE DOES NOT CARRY THIS YET. `/api/admin/corporate-hierarchy` returns the row's own columns
 * plus the two companies' contacts, and nothing from `source_party_relationships`; this returns
 * null for every live row today, and the chip renders nothing. It is written as a shape CHECK
 * rather than a cast on purpose — when the field does arrive, a payload that is not what this
 * expects renders nothing instead of `[object Object] · undefined`. A fabricated chip is worse
 * than no chip: it would assert a registry said something it did not.
 */
export function readActsAsEvidence(value: unknown): ActsAsEvidence | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as { roles?: unknown; sources?: unknown };
  if (!Array.isArray(raw.roles) || typeof raw.sources !== "string") return null;
  // Kept to the FIVE words `acts_as` admits, not merely to non-empty strings. A registry's own
  // vocabulary is wider than ours — `mlc_administrator_for_publisher` and friends — and the
  // editor renders an unrecognised draft value as a fallback <option>, so an unfiltered role
  // would give the operator a "Use this" button whose Save is answered with a 422 by Pydantic
  // and a 23514 by the CHECK. Dropping the word shows "Not recorded", which is true.
  const roles = raw.roles.filter((role): role is CorporateHierarchyActsAs =>
    typeof role === "string" && (ACTS_AS as readonly string[]).includes(role)
  );
  const sources = raw.sources.trim();
  if (!roles.length || !sources) return null;
  return { roles, sources };
}

/**
 * Does this look like ONE email address? The same shape rule the server enforces
 * (`clean_contact_email`) and the database backstops, repeated here only to spare the operator a
 * round trip. The SERVER is the authority — this never gates what is sent, it only decides whether
 * to complain before sending.
 */
export function isEmailShape(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Strip the separators an identifier is PRINTED with. Spaces, dashes and dots carry no
 * information in an IPI or an ISNI — an ISNI is published in groups of four purely for reading —
 * so removing them is safe in a way that tidying an email address is not.
 */
function compactIdentifier(value: string): string {
  return value.trim().replace(/[\s.\-]/g, "");
}

/**
 * Does this look like an IPI/CAE? 9-11 digits once the separators are gone. The 9-digit form is
 * the older CAE, the 11-digit form the IPI that replaced it.
 *
 * The SERVER is the authority (`clean_ipi_number`) and the database backstops it; this exists only
 * to spare the operator a round trip, and never gates what is sent.
 */
export function isIpiShape(value: string): boolean {
  return /^[0-9]{9,11}$/.test(compactIdentifier(value));
}

/**
 * Does this look like an ISNI? 16 characters — 15 digits then a check character that is a digit
 * or an X. Same division of labour as `isIpiShape`: the server refuses, this only warns early.
 */
export function isIsniShape(value: string): boolean {
  return /^[0-9]{15}[0-9X]$/.test(compactIdentifier(value).toUpperCase());
}

/**
 * An ISNI for display, in the published groups of four: `0000000121032683` reads as
 * `0000 0001 2103 2683`. Sixteen unbroken digits are unreadable and impossible to check against a
 * registry page by eye, which is the only thing anyone does with this number.
 *
 * Grouping is applied only to a value of the RIGHT LENGTH. Anything else is printed exactly as it
 * is stored: a stored value that does not fit the shape is a fact worth seeing, not one to dress
 * up into looking well-formed.
 */
export function formatIsni(isni: string | null | undefined): string {
  const value = (isni ?? "").trim();
  if (!isIsniShape(value)) return value;
  return (compactIdentifier(value).toUpperCase().match(/.{4}/g) ?? []).join(" ");
}

// ─────────────────────────────────────────────────────────────────────────────
// Grouping — the GROUP is the unit of this map, not the row
//
// 408 links over 26 parents: `Sony Music Publishing Group` is the parent on 94 of them. A flat
// list renders one company per row, alphabetically, so a group is never visible as one thing and
// the question the map exists to answer — "I am clearing in Germany, who do I write to?" — takes
// nine pages of scrolling. Everything below turns rows into that shape, in pure functions, so the
// ordering rules are testable without rendering anything.
// ─────────────────────────────────────────────────────────────────────────────

/** One country's presence inside a group: the chip strip, and the section heads. */
export interface TerritoryTally {
  /** null ⇒ GLOBAL. Never "unknown". */
  territory: string | null;
  /** ISO-3166-1 alpha-2, or null for a region that has none (Benelux, Scandinavia). */
  territoryCode: string | null;
  /** `formatTerritory(territory)` — "Global (all territories)" for the null case. */
  label: string;
  count: number;
}

export interface HierarchyTerritorySection extends TerritoryTally {
  rows: CorporateHierarchyRow[];
}

export interface HierarchyGroup {
  /** `parent_normalized`, or "" for the companies we hold no parent for. */
  key: string;
  /** The parent as it is displayed. "" when there is none on file. */
  parentName: string;
  hasParent: boolean;
  rows: CorporateHierarchyRow[];
  /** Global first, then by size. */
  sections: HierarchyTerritorySection[];
  /** The same order, without the rows — the collapsed card's chip strip. */
  territories: TerritoryTally[];
  /**
   * The relationship the card states ONCE — the group's most common mark. Only meaningful when
   * `statesMark` is true.
   */
  mark: string;
  /**
   * True when the card says anything about the link at all, which needs a STRICT MAJORITY. A card
   * split evenly between two marks has no "mostly", and claiming one would state the more
   * alphabetically fortunate word as the group's character.
   */
  statesMark: boolean;
  /** True when every row carries `mark`, so no row has to repeat it. */
  uniformMark: boolean;
  /** How many of `rows` name a company we can actually reach. */
  contactsOnFile: number;
  /** The parent's own company facts, which every row in the group repeats. */
  parentContactEmail: string | null;
  parentIpiNumber: string | null;
  parentIsni: string | null;
}

/** Global first, then the biggest country, then alphabetically so the order never wobbles. */
function compareTerritories(a: TerritoryTally, b: TerritoryTally): number {
  if ((a.territory === null) !== (b.territory === null)) {
    return a.territory === null ? -1 : 1;
  }
  if (a.count !== b.count) return b.count - a.count;
  return a.label.localeCompare(b.label);
}

/** The territory as a grouping key. Blank and null are the same fact — GLOBAL. */
function territoryKey(row: CorporateHierarchyRow): string | null {
  const value = (row.territory ?? "").trim();
  return value || null;
}

/**
 * Turn the flat map into parent-keyed cards.
 *
 * THE COMPANIES WITH NO PARENT ARE NOT DROPPED. They collect in one card of their own, keyed "",
 * placed LAST, and titled "No parent on file" — which is a worklist, not a group, and never a
 * claim that those companies stand alone. Losing them would make the page silently narrower than
 * the table it edits.
 */
/**
 * The group a row belongs to — the key `groupByParent` cards by and the admin assign sends.
 *
 * Keyed on the NORMALISED name the server computes, never the display name: the map's own key
 * ignores case and punctuation, so `SONY/ATV MUSIC PUBLISHING` and `Sony-ATV Music Publishing`
 * are one parent and must not become two cards.
 */
export function groupKeyOf(row: Pick<CorporateHierarchyRow, "parent_normalized" | "parent_name">): string {
  return (row.parent_normalized || row.parent_name || "").trim();
}

export function groupByParent(rows: CorporateHierarchyRow[]): HierarchyGroup[] {
  const buckets = new Map<string, CorporateHierarchyRow[]>();
  for (const row of rows) {
    const key = groupKeyOf(row);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(row);
    else buckets.set(key, [row]);
  }

  const groups: HierarchyGroup[] = [];
  for (const [key, bucketRows] of buckets) {
    const sectionsByKey = new Map<string, HierarchyTerritorySection>();
    const marks = new Map<string, number>();
    let contactsOnFile = 0;

    for (const row of bucketRows) {
      const territory = territoryKey(row);
      const sectionKey = territory ?? "";
      const section = sectionsByKey.get(sectionKey);
      if (section) {
        section.rows.push(row);
        section.count += 1;
        // A code only some rows carry is still the country's code.
        if (!section.territoryCode && row.territory_code) {
          section.territoryCode = row.territory_code;
        }
      } else {
        sectionsByKey.set(sectionKey, {
          territory,
          territoryCode: row.territory_code,
          label: formatTerritory(territory),
          count: 1,
          rows: [row],
        });
      }
      const mark = (row.relationship || "").trim() || "owns";
      marks.set(mark, (marks.get(mark) ?? 0) + 1);
      if (row.child_contact_email) contactsOnFile += 1;
    }

    const sections = [...sectionsByKey.values()].sort(compareTerritories);
    // Ties broken alphabetically so the ORDER is deterministic; whether the winner is stated at
    // all is decided by the strict-majority test below, not by this sort.
    const [mark, markCount] = [...marks.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    )[0];
    const first = bucketRows[0];

    groups.push({
      key,
      parentName: (first.parent_name ?? "").trim(),
      hasParent: Boolean(key),
      rows: bucketRows,
      sections,
      territories: sections.map(({ rows: _rows, ...tally }) => tally),
      mark,
      statesMark: markCount * 2 > bucketRows.length,
      uniformMark: markCount === bucketRows.length,
      contactsOnFile,
      // Company facts, so every row in the bucket carries the same three values.
      parentContactEmail: first.parent_contact_email,
      parentIpiNumber: first.parent_ipi_number,
      parentIsni: first.parent_isni,
    });
  }

  return groups.sort((a, b) => {
    // The no-parent card is the absence of a group, so it never competes for the top on size.
    if (a.hasParent !== b.hasParent) return a.hasParent ? -1 : 1;
    if (a.rows.length !== b.rows.length) return b.rows.length - a.rows.length;
    return a.parentName.localeCompare(b.parentName);
  });
}

/** A group's rows A–Z, ignoring country — the flat view of one card. */
export function groupRowsAlphabetically(
  group: HierarchyGroup
): CorporateHierarchyRow[] {
  return [...group.rows].sort((a, b) =>
    a.child_name.localeCompare(b.child_name)
  );
}

/**
 * The rows a search matches: either company name, the country, or its code.
 *
 * IN MEMORY, over the whole map, because the whole map is loaded — so one query reaches across all
 * 26 groups at once, which is the thing a server-side `q` on a 50-row page could not do. Typing
 * "germany" answers "who do I write to in Germany" across every group in one keystroke.
 */
export function filterHierarchyRows(
  rows: CorporateHierarchyRow[],
  query: string
): CorporateHierarchyRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    [
      row.child_name,
      row.parent_name,
      // The LABEL, not the raw column: "global" must find the rows whose territory is NULL, which
      // is exactly the case no text filter over the stored value can ever reach.
      formatTerritory(row.territory),
      row.territory_code ?? "",
    ]
      .join(" ")
      .toLowerCase()
      .includes(needle)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Assign a reference group to an organization (platform admin only)
// ─────────────────────────────────────────────────────────────────────────────

/** Is this row ZenMusic-filed (curated)? Self-added rows belong to the org that added them. */
export function isCuratedRow(row: Pick<CorporateHierarchyRow, "source_label">): boolean {
  return row.source_label !== "org_self_served";
}

/** What assigning a group would touch — read-only, the staff gate's footprint. */
export interface AssignGroupPreview {
  group: string;
  total_in_group: number;
  /** Curated rows with no organization yet — what the assign would write. */
  curated_unassigned: number;
  /** Organizations that already own curated rows of this group. */
  assigned_to: { id: string; name: string; rows: number }[];
  self_added: number;
  /** Unclaimed, non-merged parties the curated rows name. */
  unclaimed_parties: number;
  /** Active confirmed rights naming those parties — what claims here would repoint. */
  confirmed_rights: number;
}

export interface AssignGroupResult {
  group: string;
  organization: { id: string; name: string };
  assigned: number;
  already_assigned: number;
  skipped_self_added: number;
  total_in_group: number;
}

/** `group` is the group's key (`parent_normalized`); the server re-normalizes it. */
export async function fetchAssignGroupPreview(
  group: string,
  options: { signal?: AbortSignal } = {}
): Promise<AssignGroupPreview> {
  return apiClient.get<AssignGroupPreview>(
    `/api/admin/corporate-hierarchy/groups/assign-preview?group=${encodeURIComponent(group)}`,
    { signal: options.signal }
  );
}

/**
 * Put a reference group's ZenMusic-filed rows into an organization's map. Curated rows with no
 * organization only; self-added rows are never touched. 409 `group_owned_elsewhere` when another
 * org owns curated rows of the group (its body carries `assigned` when this call wrote some first).
 */
export async function assignCorporateHierarchyGroup(
  group: string,
  organizationId: string
): Promise<AssignGroupResult> {
  return apiClient.post<AssignGroupResult>("/api/admin/corporate-hierarchy/groups/assign", {
    group,
    organization_id: organizationId,
  });
}

function companies(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "company" : "companies"}`;
}

/** The assign confirm (contract §8.8), built from the PREVIEW's numbers — never predicted. */
export function assignConfirmCopy(
  preview: AssignGroupPreview,
  groupName: string,
  organization: { id: string; name: string }
): { question: string; lines: string[]; blockedBy: string | null } {
  const org = organization.name;
  const other = preview.assigned_to.find((o) => o.id !== organization.id) ?? null;
  return {
    question: `Assign ${groupName} (${companies(preview.curated_unassigned)}) to ${org}?`,
    lines: [
      `${org}'s team will see these companies in Your companies and can claim each one themselves, and a claim can't be undone from their side.`,
      preview.confirmed_rights > 0
        ? `Claims here would repoint ${preview.confirmed_rights.toLocaleString("en-US")} confirmed rights that name these companies, on any label's catalog, to ${org}.`
        : "No confirmed rights name these companies today.",
      "There's no undo button for this assignment yet.",
    ],
    // The POST would 409: say so instead of offering the button.
    blockedBy: other
      ? `${companies(other.rows)} in ${groupName} already belong to ${other.name}, so this group can't be assigned to ${org}.`
      : null,
  };
}

/** What the assign did, from the server's own counts. */
export function assignResultLine(result: AssignGroupResult): string {
  const lead = `Assigned ${companies(result.assigned)} to ${result.organization.name}.`;
  return result.skipped_self_added > 0
    ? `${lead} ${companies(result.skipped_self_added)} another organization added itself ${result.skipped_self_added === 1 ? "was" : "were"} left alone.`
    : lead;
}
